import { useState, type FormEvent } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import {
  EPAYMENT_PROVIDERS,
  type CreateElectronicsProviderInput,
  type ElectronicsProviderDto,
  type EPaymentProvider,
  type UpdateElectronicsProviderInput,
} from '@gc/shared';

import { api } from '@/lib/api';
import { PageHeading, Toggle, ghostPillClass, inputClass, pillClass } from './controls';
import { toFieldErrors, useResource, type FieldError } from './useSettings';

/**
 * Payment providers (SPEC §6.8; owner, 2026-09-23: make the page work).
 *
 * A switched-on provider is a TENDER: it appears at the till and in the
 * refill dialog beside Cash and Account balance, and a payment taken through
 * it is recorded as electronic money — its own column in the box, never cash.
 * There is no connection to the providers themselves (no API documentation or
 * merchant credentials exist yet), and the page says so in one line: the
 * cashier confirms the payment in the provider's app, then records it here.
 *
 * The API key and secret are WRITE-ONLY. The server never sends them back, so
 * their boxes start empty: blank keeps what is stored, "Remove" clears it.
 *
 * Called "Electronics" until 2026-09-20; the route moved to
 * `/settings/payments` with the old path redirecting, while the API path and
 * the `settings.electronics.*` locale keys kept the old name.
 */
export function PaymentProvidersPage() {
  const { t } = useTranslation();
  const { data, error, reload } = useResource<ElectronicsProviderDto[]>('/settings/electronics');
  /** The provider being edited, `'new'` for Add, null when no dialog is open. */
  const [editing, setEditing] = useState<ElectronicsProviderDto | 'new' | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  async function remove(provider: ElectronicsProviderDto): Promise<void> {
    if (!window.confirm(t('settings.electronics.deleteConfirm', { label: provider.label }))) return;
    setRowError(null);
    try {
      await api.delete(`/settings/electronics/${provider.id}`);
      await reload();
    } catch (caught) {
      // `electronics_provider_in_use` says in so many words to switch it off.
      setRowError(toFieldErrors(caught, t('settings.electronics.deleteFailed'))[0]!.message);
    }
  }

  return (
    <div className="p-6">
      <PageHeading
        title={t('settings.nav.payments')}
        description={t('settings.electronics.description')}
      />

      <div className="max-w-2xl space-y-4">
        <ProviderList
          providers={data}
          error={error}
          rowError={rowError}
          onAdd={() => setEditing('new')}
          onEdit={(provider) => setEditing(provider)}
          onDelete={(provider) => void remove(provider)}
        />
      </div>

      {editing ? (
        <ProviderDialog
          provider={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => void reload()}
        />
      ) : null}
    </div>
  );
}

/**
 * The configured providers, and the one line about what "works" means here.
 * Exported so the render test can pin it without fetching.
 */
export function ProviderList({
  providers,
  error = null,
  rowError = null,
  onAdd,
  onEdit,
  onDelete,
}: {
  providers: ElectronicsProviderDto[] | null;
  error?: string | null;
  rowError?: string | null;
  onAdd: () => void;
  onEdit: (provider: ElectronicsProviderDto) => void;
  onDelete: (provider: ElectronicsProviderDto) => void;
}) {
  const { t } = useTranslation();

  return (
    <section className="rounded-card border border-subtle bg-raised p-4">
      <p data-no-gateway className="mb-3 text-sm text-content-secondary">
        {t('settings.electronics.noGateway')}
      </p>

      <div className="mb-3 flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold uppercase tracking-wide text-content-secondary">
          {t('settings.electronics.providers')}
        </h2>
        <button type="button" onClick={onAdd} className={`${pillClass} flex items-center gap-1.5`}>
          <Plus className="h-4 w-4" aria-hidden />
          {t('settings.electronics.add')}
        </button>
      </div>

      {/* Error before empty: a failed load must not read as "none configured". */}
      {error ? (
        <p role="alert" className="text-sm text-danger">
          {error}
        </p>
      ) : providers === null ? (
        <p className="text-sm text-content-muted">{t('common.loading')}</p>
      ) : providers.length === 0 ? (
        <p className="text-sm text-content-muted">{t('settings.electronics.empty')}</p>
      ) : (
        <ul className="space-y-2">
          {providers.map((provider) => (
            <li
              key={provider.id}
              data-provider={provider.id}
              className="flex flex-wrap items-center gap-3 rounded-control border border-subtle bg-input px-3 py-2"
            >
              <span className="min-w-0 flex-1">
                <span className="flex items-center gap-2 text-sm text-content">
                  <span className="truncate font-medium">{provider.label}</span>
                  <span className="text-xs text-content-muted">
                    {t(`settings.electronics.provider.${provider.provider}`)}
                  </span>
                </span>
                <span className="block text-xs text-content-muted">
                  {credentialLine(provider, t)}
                  {provider.merchantId
                    ? ` · ${t('settings.electronics.merchantId')}: ${provider.merchantId}`
                    : ''}
                </span>
              </span>
              <span
                data-active={provider.isActive}
                className={`rounded-full px-2 py-0.5 text-xs ${
                  provider.isActive ? 'bg-primary/15 text-primary' : 'bg-hover text-content-muted'
                }`}
              >
                {provider.isActive ? t('settings.electronics.on') : t('settings.electronics.off')}
              </span>
              <button
                type="button"
                onClick={() => onEdit(provider)}
                aria-label={t('settings.electronics.editTitle', { label: provider.label })}
                className="rounded-control p-1.5 text-content-secondary hover:bg-hover hover:text-content"
              >
                <Pencil className="h-4 w-4" aria-hidden />
              </button>
              <button
                type="button"
                onClick={() => onDelete(provider)}
                aria-label={t('common.delete')}
                className="rounded-control p-1.5 text-content-secondary hover:bg-hover hover:text-danger"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
              </button>
            </li>
          ))}
        </ul>
      )}

      {rowError ? (
        <p role="alert" className="mt-3 text-sm text-danger">
          {rowError}
        </p>
      ) : null}

      <p className="mt-3 text-xs text-content-muted">{t('settings.electronics.encryptionNote')}</p>
    </section>
  );
}

function credentialLine(
  provider: ElectronicsProviderDto,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const parts = [
    provider.hasApiKey ? t('settings.electronics.keyStored') : null,
    provider.hasApiSecret ? t('settings.electronics.secretStored') : null,
  ].filter((part): part is string => part !== null);
  return parts.length > 0 ? parts.join(' · ') : t('settings.electronics.noCredentials');
}

/* -------------------------------------------------------------------------- */
/* Add / edit                                                                 */
/* -------------------------------------------------------------------------- */

/** What the dialog's fields hold, as typed. */
export interface ProviderForm {
  provider: EPaymentProvider;
  label: string;
  merchantId: string;
  baseUrl: string;
  /** Typed only; the stored value never comes back. Blank = keep. */
  apiKey: string;
  apiSecret: string;
  clearApiKey: boolean;
  clearApiSecret: boolean;
  isActive: boolean;
}

export function providerFormOf(provider: ElectronicsProviderDto | null): ProviderForm {
  return {
    provider: provider?.provider ?? 'FIB',
    label: provider?.label ?? '',
    merchantId: provider?.merchantId ?? '',
    baseUrl: provider?.baseUrl ?? '',
    apiKey: '',
    apiSecret: '',
    clearApiKey: false,
    clearApiSecret: false,
    isActive: provider?.isActive ?? true,
  };
}

/** The Add body. A blank credential is left out: none stored. */
export function createProviderPayload(form: ProviderForm): CreateElectronicsProviderInput {
  return {
    provider: form.provider,
    label: form.label.trim(),
    merchantId: form.merchantId.trim() || null,
    baseUrl: form.baseUrl.trim() || null,
    ...(form.apiKey.trim() ? { apiKey: form.apiKey } : {}),
    ...(form.apiSecret.trim() ? { apiSecret: form.apiSecret } : {}),
    isActive: form.isActive,
  };
}

/**
 * The Save body. A credential is SENT only when it changes: retyped (the new
 * value) or removed (null). Left blank, it is not in the body at all and the
 * server keeps what it holds — the box starts empty because the stored value
 * never comes back, and saving a new name must not wipe a key nobody retyped.
 */
export function updateProviderPayload(form: ProviderForm): UpdateElectronicsProviderInput {
  const credential = (typed: string, clear: boolean): { value?: string | null } =>
    clear ? { value: null } : typed.trim() ? { value: typed } : {};
  const key = credential(form.apiKey, form.clearApiKey);
  const secret = credential(form.apiSecret, form.clearApiSecret);
  return {
    label: form.label.trim(),
    merchantId: form.merchantId.trim() || null,
    baseUrl: form.baseUrl.trim() || null,
    ...('value' in key ? { apiKey: key.value } : {}),
    ...('value' in secret ? { apiSecret: secret.value } : {}),
    isActive: form.isActive,
  };
}

/**
 * Add or edit a provider. Portalled to <body> like every settings dialog, so
 * its <form> can never end up nested inside a page's.
 */
export function ProviderDialog({
  provider,
  onClose,
  onSaved,
}: {
  provider: ElectronicsProviderDto | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { t } = useTranslation();
  const isEdit = provider !== null;

  const [form, setForm] = useState<ProviderForm>(() => providerFormOf(provider));
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [submitting, setSubmitting] = useState(false);

  const set = <K extends keyof ProviderForm>(key: K, value: ProviderForm[K]) =>
    setForm((current) => ({ ...current, [key]: value }));
  const errorFor = (field: string) => errors.find((issue) => issue.field === field)?.message;

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    setErrors([]);
    setSubmitting(true);
    try {
      if (isEdit) {
        await api.patch(`/settings/electronics/${provider.id}`, updateProviderPayload(form));
      } else {
        await api.post('/settings/electronics', createProviderPayload(form));
      }
      onSaved();
      onClose();
    } catch (caught) {
      setErrors(toFieldErrors(caught, t('settings.electronics.saveFailed')));
    } finally {
      setSubmitting(false);
    }
  }

  return createPortal(
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={(event) => void handleSubmit(event)}
        className="max-h-full w-full max-w-md overflow-auto rounded-card border border-subtle bg-raised p-5 shadow-2xl"
      >
        <h2 className="mb-4 text-lg font-semibold text-content">
          {isEdit
            ? t('settings.electronics.editTitle', { label: provider.label })
            : t('settings.electronics.addTitle')}
        </h2>

        <div className="space-y-3">
          <label className="block">
            <span className="mb-1 block text-sm text-content-secondary">
              {t('settings.electronics.kind')}
            </span>
            {/* The kind is fixed once created: payments already name the
                provider. The name is what must be unique — across every kind,
                since the till and "Paid by …" show the name alone. */}
            <select
              value={form.provider}
              disabled={isEdit}
              onChange={(event) => set('provider', event.target.value as EPaymentProvider)}
              className={inputClass}
            >
              {EPAYMENT_PROVIDERS.map((kind) => (
                <option key={kind} value={kind}>
                  {t(`settings.electronics.provider.${kind}`)}
                </option>
              ))}
            </select>
          </label>

          <TextField
            label={t('settings.electronics.label')}
            hint={t('settings.electronics.labelHint')}
            error={errorFor('label')}
            value={form.label}
            onChange={(value) => set('label', value)}
            required
            maxLength={60}
          />
          <TextField
            label={t('settings.electronics.merchantId')}
            error={errorFor('merchantId')}
            value={form.merchantId}
            onChange={(value) => set('merchantId', value)}
            maxLength={120}
          />
          <TextField
            label={t('settings.electronics.baseUrl')}
            error={errorFor('baseUrl')}
            value={form.baseUrl}
            onChange={(value) => set('baseUrl', value)}
            maxLength={300}
            placeholder="https://"
          />

          <CredentialField
            label={t('settings.electronics.apiKey')}
            clearLabel={t('settings.electronics.clearApiKey')}
            stored={provider?.hasApiKey === true}
            value={form.apiKey}
            cleared={form.clearApiKey}
            error={errorFor('apiKey')}
            onChange={(value) => set('apiKey', value)}
            onClear={(value) => set('clearApiKey', value)}
          />
          <CredentialField
            label={t('settings.electronics.apiSecret')}
            clearLabel={t('settings.electronics.clearApiSecret')}
            stored={provider?.hasApiSecret === true}
            value={form.apiSecret}
            cleared={form.clearApiSecret}
            error={errorFor('apiSecret')}
            onChange={(value) => set('apiSecret', value)}
            onClear={(value) => set('clearApiSecret', value)}
          />
          <p className="text-xs text-content-muted">{t('settings.electronics.credentialHint')}</p>

          <Toggle
            label={t('settings.electronics.active')}
            hint={t('settings.electronics.activeHint')}
            checked={form.isActive}
            onChange={(value) => set('isActive', value)}
          />
        </div>

        {errorFor('(root)') ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {errorFor('(root)')}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className={ghostPillClass}>
            {t('common.cancel')}
          </button>
          <button type="submit" disabled={submitting} className={pillClass}>
            {isEdit ? t('common.save') : t('common.add')}
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}

function TextField({
  label,
  hint,
  error,
  value,
  onChange,
  required,
  maxLength,
  placeholder,
}: {
  label: string;
  hint?: string;
  error?: string;
  value: string;
  onChange: (value: string) => void;
  required?: boolean;
  maxLength: number;
  placeholder?: string;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm text-content-secondary">{label}</span>
      <input
        value={value}
        onChange={(event) => onChange(event.target.value)}
        required={required}
        maxLength={maxLength}
        placeholder={placeholder}
        className={inputClass}
      />
      {hint ? <span className="mt-1 block text-xs text-content-muted">{hint}</span> : null}
      {error ? <span className="mt-1 block text-xs text-danger">{error}</span> : null}
    </label>
  );
}

/**
 * A write-only credential: an empty password box, a placeholder saying
 * whether one is stored, and — only when one is — a "Remove" tick.
 */
function CredentialField({
  label,
  clearLabel,
  stored,
  value,
  cleared,
  error,
  onChange,
  onClear,
}: {
  label: string;
  clearLabel: string;
  stored: boolean;
  value: string;
  cleared: boolean;
  error?: string;
  onChange: (value: string) => void;
  onClear: (value: boolean) => void;
}) {
  const { t } = useTranslation();

  return (
    <div>
      <label className="block">
        <span className="mb-1 block text-sm text-content-secondary">{label}</span>
        <input
          type="password"
          // A browser must never offer to save or fill a provider's key.
          autoComplete="new-password"
          value={value}
          disabled={cleared}
          onChange={(event) => onChange(event.target.value)}
          maxLength={500}
          placeholder={stored ? t('settings.electronics.credentialStored') : ''}
          className={inputClass}
        />
      </label>
      {stored ? (
        <label className="mt-1 flex items-center gap-2 text-xs text-content-secondary">
          <input
            type="checkbox"
            checked={cleared}
            onChange={(event) => onClear(event.target.checked)}
            className="h-3.5 w-3.5 accent-[var(--brand-primary)]"
          />
          {clearLabel}
        </label>
      ) : null}
      {error ? <span className="mt-1 block text-xs text-danger">{error}</span> : null}
    </div>
  );
}
